import { useMutation } from "@apollo/client";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState } from "react";
import { View } from "react-native";
import { Text } from "react-native-paper";
import { ActionsContainer, Button } from "../../../components";
import Modal from "../../../components/Modal";
import config from "../../../config";
import { formatCurrency } from "../../utils";
import { OrderForm } from "./OrderForm";
import {
  Props,
  MUTATION,
  MutationInput,
  MutationResponseType,
  PaymentIntentResponseType,
  PaymentMethod,
  PROXY_MUTATION,
} from "./types";

const CheckoutModal = ({
  paymentIntentResponse,
  onDismiss,
  onComplete,
}: {
  paymentIntentResponse: PaymentIntentResponseType;
  onDismiss(): void;
  onComplete(): void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);

  async function checkout() {
    setLoading(true);
    setErrorMessage(undefined);
    const { error: submitError } = await elements!.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      return;
    }

    const confirmParams =
      paymentIntentResponse.customerName && paymentIntentResponse.customerEmail
        ? {
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: paymentIntentResponse.customerName,
                  email: paymentIntentResponse.customerEmail,
                },
              },
            },
          }
        : {};

    const result = await stripe!.confirmPayment({
      elements: elements!,
      clientSecret: paymentIntentResponse.paymentIntentClientSecret,
      redirect: "if_required",
      ...confirmParams,
    });

    if (result.error) {
      setErrorMessage(result.error.message);
    } else {
      onComplete();
    }
    setLoading(false);
  }

  return (
    <Modal visible title="Payment Information" hasBorder onDismiss={onDismiss}>
      <View className="p-4">
        {!!errorMessage && <Text className="text-red-dark py-2">{errorMessage}</Text>}
        <PaymentElement />
        <View className="mt-8" />
        <ActionsContainer insideModal>
          <Button testID="pay-button" mode="contained" loading={loading} className="w-full" onPress={checkout}>
            Pay {formatCurrency({ value: paymentIntentResponse.total / 100, unit: "USD" })}
          </Button>
        </ActionsContainer>
      </View>
    </Modal>
  );
};

function DuesScreen({ proxyId, order, onComplete }: Props) {
  const [paymentIntentResponse, setPaymentIntentResponse] = useState<PaymentIntentResponseType>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [createPaymentIntent] = useMutation<MutationResponseType>(proxyId ? PROXY_MUTATION : MUTATION);
  const [errorMessage, setErrorMessage] = useState<string>();

  const openCheckout = async (input: MutationInput) => {
    try {
      const { data } = await createPaymentIntent({ variables: { input: { ...input, proxyId } } });
      setPaymentMethod(input.paymentMethod);
      setPaymentIntentResponse(data?.createPaymentIntent);
    } catch (error: any) {
      setErrorMessage(error.message || "Unexpected error. Please try again.");
    }
  };

  const stripePromise = loadStripe(config.stripePublishableKey[order.stripeAccount]);

  return (
    <>
      {!!errorMessage && <Text className="text-red-dark font-400 text-regular-normal p-2">{errorMessage}</Text>}
      <OrderForm order={order} isProxy={!!proxyId} openCheckout={openCheckout} />
      {paymentIntentResponse && (
        <Elements stripe={stripePromise} options={{ clientSecret: paymentIntentResponse.paymentIntentClientSecret }}>
          <CheckoutModal
            paymentIntentResponse={paymentIntentResponse}
            onDismiss={() => setPaymentIntentResponse(undefined)}
            onComplete={() => onComplete(paymentMethod!)}
          />
        </Elements>
      )}
    </>
  );
}

export default DuesScreen;
